<template>
  <div ref="placeRef">
    <div class="place" @click="onClick()" :class="{
      'dragging': isDragging,
      'first': place.first,
      'last': place.last,
      'hideData': hideData,
      'expanded': place.live && isExpanded,
      'current': place.live?.current,
      'visited': place.live?.visited,
      'latest': place.live?.latest,
      'compact': place.live
    }">
      <div class="place-summary">
        <div class="index">{{ hideData ? '' : getPlaceSymbol(route.id, placeIndex) }}</div>
        <div class="title">
          <div class="address name text-overflow-fade">
            <template v-if="place.name">{{ place.name }}</template>
            <template v-else><i>Unavngivet stop</i></template>
          </div>
          <div class="time" :title="place.first ? 'Afgang' : 'Ankomst'">
            <template v-if="place.live && liveState.started">
              {{ $filters.time(place.first ? place.live?.times.departure : place.live?.times.arrival) }}
            </template>
            <template v-else>
              {{ $filters.time(place.first ? place.times.departure : place.times.arrival) }}
            </template>
          </div>
        </div>
        <div class="subtitle">
          <div class="address text-overflow-fade">{{ place.address.replace(/,[^,]*$/, "") }}</div>
          <div class="time" :title="'Afgang'">
            <template v-if="!(place.first || place.last)">
              <template v-if="!place.times.break && place.breakTime == -1"></template>
              <template v-if="place.live && liveState.started"> {{ $filters.time(place.live?.times.departure || place.times.departure + liveState.timeOffset) }} </template>
              <template v-else> <span :class="{'custom': !(place.first || place.last) && place.breakTime >= 0}">{{$filters.duration(place.times.break)}} </span> → {{ $filters.time(place.times.departure) }} </template>
            </template>
          </div>
        </div>
      </div>

      <div class="place-dropdown" v-if="!place.live && isOwner && canEdit" style="margin-left:.5rem;">
        <AppDropdown>
            <AppButton @click="setFirst" v-if="!place.first" :icon="'vertical_align_top'" >{{ $t('place.set_first') }}</AppButton>
            <AppButton @click="setLast" v-if="!place.last" :icon="'vertical_align_bottom'" >{{ $t('place.set_last') }}</AppButton>
            <AppButton @click="edit()" :icon="'create'" >{{ $t('place.edit') }}</AppButton>
            <AppButton @click="focus()" :icon="'zoom_in'" class="no-mobile">{{ $t('place.focus') }}</AppButton>
            <AppButton @click="remove" :icon="'delete'" class="color-error" >{{ $t('place.delete') }}</AppButton>
        </AppDropdown>
      </div>
      
      <div class="place-drag" v-if="false"><AppIcon name="drag_indicator" /></div>

      <div class="place-custom-break" v-if="!(place.first || place.last) && place.breakTime >= 0" v-tooltip="'Speciel pause'">
        <AppIcon name="timer" />
      </div>
    </div>

  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, watch, ref, computed, onMounted } from 'vue'
import { store } from '@/store'
import AppDropdown from '@/components/atoms/AppDropdown.vue'

import { getPlaceSymbol } from '@/api/tripply'
import { Route, Place, RouteLiveState } from '@/api/tripply/models'

export default defineComponent({
  name: 'RoutePlace',
  components: { AppDropdown },
  props: {
    route: {
      type: Object as PropType<Route>,
      required: true
    },
    place: {
      type: Object as PropType<Place>,
      required: true
    },
    placeIndex: {
      type: Number,
      required: true
    },
    liveState: {
      type: [Object, Boolean] as PropType<RouteLiveState>,
      required: true
    },
    isDragging: Boolean,
    hideData: Boolean,
    canEdit: Boolean
  },
  setup(props) {
    const placeRef = ref<HTMLElement | null>(null)
    const isOwner = computed(() => store.user.isOwner(props.route.id))
    const isExpanded = ref(false)

    function edit() {
      props.canEdit && store.modal.showModal('PlaceSettings', {routeId: props.route.id, placeIndex: props.placeIndex, place: props.place})
    }
    function setFirst() {
      store.route.setPlaceFirst(props.route.id, props.placeIndex)
    }
    function setLast() {
      store.route.setPlaceLast(props.route.id, props.placeIndex)
    }
    function remove() {
      store.route.deletePlace(props.route.id, props.placeIndex)
    }
    function onClick() {
      isExpanded.value = !isExpanded.value
    }
    function focus() {
      store.map.focus(props.place)
    }

    function scrollIntoView() {
      if (props.place.live?.latest) {
        placeRef.value && placeRef.value.scrollIntoView()
      }
    }
    
    watch(() => props.liveState, () => {
      scrollIntoView()
    })

    onMounted(() => {
      setTimeout(() => {
        scrollIntoView()
      }, 2000)
    })

    return { isOwner, onClick, focus, remove, edit, setFirst, setLast, isExpanded, getPlaceSymbol, placeRef }
  }
})
</script>

<style lang="scss" scoped>

.ghost .place, .sortable-chosen .place {
  &:after {
    opacity: 1;
    background: var(--color-card-elevated) !important;
    box-shadow: var(--card-shadow);
  }
  padding-right: .75rem;
  z-index: 100000;
  transform: scale(1.025) rotate(-1deg);
  cursor: grabbing !important;
}


.place {
  height: 4rem;
  border-radius: 1rem;
  padding: .75rem .0 0 3.25rem;
  position: relative;
  transition: height .2s, transform .2s, padding .2s;
  user-select: none;
  width: 100%;
  display: flex;
  justify-content: space-between;
  transform-origin: top left;
  
  &-drag {
    opacity: 0.5;
    margin-top: .5rem;
    &:hover {
      opacity: 1;
    }
  }
  .index, &-drag {
    cursor: grab;
    &:active {
      cursor: grabbing;
    }
  }


  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: -.5rem;
    right: 0;
    bottom: 0;
    opacity: 0;
    z-index: -1;
    border-radius: 1rem;
    transition: opacity .2s;
  }

  &-summary {
    overflow: hidden;
    width: 100%;
    transition: width .2s;
  }
  &-dropdown {
  }

  &:before {
    content: "";
    position: absolute;
    left: 18px;
    top: 1.5rem;
    bottom: -1.5rem;
    z-index: 1001;
    width: .25rem;
    background: #06f;
    transform-origin: top center;
    transform: scaleY(0);
    transition: transform .3s;
  }

  &:hover {
    &:after {
      //opacity: 1;
    }

    .place-summary {
      // width: calc(100% - 4rem);
    }
  }
  &.expanded {
    height: 8rem !important;
  }

  &.compact {
    height: 3rem;
    .subtitle {
      opacity: 0 !important;
    }
    &.expanded {
      height: 4.25rem !important;
      .subtitle {
        opacity: 1 !important;
      }
    }
  }

  &.first .index, &.last .index {
    transform: scale(1.25);
  }
  &.current {
    .index {
      transform: scale(1.25);
    }
  }
  &.visited {
    .index {
      transform: scale(0.5);
      color: transparent;
    }
    .title, .subtitle {
      opacity: 0.5;
    }
    &:before {
      transform: scaleY(1);
    }
  }
  &.latest {
    &:before {
      transform: scaleY(.5);
    }
  }

  &-desc {
    font-size: .9375rem;    
  }
  &-custom-break {
    position: absolute;
    color: #06f;
    border-radius: 1rem;
    padding: 0.1rem;
    top: .5rem;
    left: -1.25rem;
  }

  .index {
    z-index: 10000;
    transition: all .2s;
    transform-origin: center center;
    position: absolute;
    left: .5rem;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 1rem;
    background: var(--color-primary);
    color: var(--color-on-primary);
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: .875rem;
    font-weight: 600;
    line-height: 1;
  }
  .title, .subtitle {
    display: flex;
    justify-content: space-between;
    white-space: nowrap;

    .address {
    }

    .time {
      transition: transform .2s, opacity .2s;
      text-align: right;
      padding-left: .5rem;
    }
  }
  .title {
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.5rem;
  }
  .subtitle {
    font-size: .8125rem;
    font-weight: 400;
    line-height: 1rem;
    color: var(--text-color-secondary);

    .time span.custom {
      color: var(--color-primary);
      font-weight: 600;
    }
  }
}


.place {
  &.hideData {
    .index {
      transform: scale(0.25);
    }
    .title, .subtitle {
      .time {
        transform: scale(0.75);
        opacity: 0;
      }
    }
  }
}

@media only print {
  .place {
    display: table;
    break-inside: avoid;
  }
}
</style>